import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Badge, Button, Card, CardBody, Col, Input, Progress, Row, Spinner } from 'reactstrap';
import { Link, withRouter } from 'react-router-dom';
import apiBase from '../../apiConfig';
import { settingsRoutes, testSettingsItems } from '../../routes';
import { getAuthHeaders } from '../../helpers/utils';
import AppContext from '../../context/Context';
import { filterRoutesByPermissions } from '../../helpers/permissions';

const TEST_PERIOD_ENDPOINT = `${apiBase}/p-admin/test-period.php`;

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const sortNumericValues = values =>
  [...new Set((values || []).map(value => toNumber(value)).filter(value => value > 0))].sort((left, right) => left - right);

const buildPeriodKey = (yil, chorak) => `${toNumber(yil)}-${toNumber(chorak)}`;
const formatPeriodLabel = (yil, chorak) => `${yil} / ${chorak}-chorak`;

const buildPeriodMap = items =>
  (items || []).reduce((accumulator, item) => {
    accumulator[buildPeriodKey(item.yil, item.chorak)] = {
      yil: toNumber(item.yil),
      chorak: toNumber(item.chorak),
      count: toNumber(item.count)
    };
    return accumulator;
  }, {});

const buildPeriodTimeline = (years, quarters) => {
  const normalizedYears = sortNumericValues(years);
  const normalizedQuarters = sortNumericValues(quarters);
  const items = [];

  normalizedYears.forEach(yil => {
    normalizedQuarters.forEach(chorak => {
      items.push({ yil, chorak });
    });
  });

  return items;
};

const getShiftedPeriod = (current, periods, direction) => {
  const currentKey = buildPeriodKey(current.yil, current.chorak);
  const currentIndex = periods.findIndex(item => buildPeriodKey(item.yil, item.chorak) === currentKey);

  if (currentIndex === -1) {
    return null;
  }

  const nextIndex = currentIndex + direction;

  if (nextIndex < 0 || nextIndex >= periods.length) {
    return null;
  }

  return periods[nextIndex];
};

const renderSettingsGroups = (groups, locationPathname) =>
  (groups || []).map(section => (
    <div key={section.to} className="med-period-settings-group">
      <Link to={section.to} className="med-period-settings-group__head">
        <span>{section.name}</span>
        <FontAwesomeIcon icon="angle-right" />
      </Link>
      <div className="med-period-settings-group__children">
        {(section.children || []).map(child => (
          <Link
            key={child.to}
            to={child.to}
            className={classNames('med-period-settings-link', {
              'is-active': locationPathname === child.to
            })}
          >
            <span>{child.name}</span>
          </Link>
        ))}
      </div>
    </div>
  ));

const TestPeriodSettings = ({ location }) => {
  const { user } = useContext(AppContext);
  const [periodLoading, setPeriodLoading] = useState(true);
  const [periodSaving, setPeriodSaving] = useState(false);
  const [periodBanner, setPeriodBanner] = useState(null);
  const [periodPermissions, setPeriodPermissions] = useState({ view: false, manage: false });
  const [periodOptions, setPeriodOptions] = useState({ years: [], quarters: [] });
  const [periodSummary, setPeriodSummary] = useState([]);
  const [savedPeriod, setSavedPeriod] = useState({ yil: 2026, chorak: 1 });
  const [periodForm, setPeriodForm] = useState({ yil: '2026', chorak: '1' });

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );
  const visibleSettingsGroups = useMemo(
    () => filterRoutesByPermissions(settingsRoutes.children || [], user),
    [user]
  );
  const visibleTestSettingsItems = useMemo(() => filterRoutesByPermissions(testSettingsItems, user), [user]);

  useEffect(() => {
    let active = true;

    const loadPeriod = async () => {
      setPeriodLoading(true);
      setPeriodBanner(null);

      try {
        const response = await axios.get(TEST_PERIOD_ENDPOINT, requestConfig);

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          const data = response.data.data || {};
          const period = data.period || {};
          const selected = period.selected || {};
          const options = period.options || {};
          const nextPeriod = {
            yil: toNumber(selected.yil, 2026),
            chorak: toNumber(selected.chorak, 1)
          };

          setPeriodPermissions(data.permissions || { view: false, manage: false });
          setPeriodOptions({
            years: sortNumericValues(options.years || []),
            quarters: sortNumericValues(options.quarters || [])
          });
          setPeriodSummary(period.summary || []);
          setSavedPeriod(nextPeriod);
          setPeriodForm({
            yil: String(nextPeriod.yil),
            chorak: String(nextPeriod.chorak)
          });
          return;
        }

        throw new Error((response.data && response.data.message) || "Test davri sozlamalari yuklanmadi.");
      } catch (error) {
        if (!active) {
          return;
        }

        setPeriodBanner({
          color: 'danger',
          text: error.response?.data?.message || error.message || "Test davri sozlamalarini yuklashda xatolik yuz berdi."
        });
      } finally {
        if (active) {
          setPeriodLoading(false);
        }
      }
    };

    loadPeriod();

    return () => {
      active = false;
    };
  }, [requestConfig]);

  const periodMap = useMemo(() => buildPeriodMap(periodSummary), [periodSummary]);
  const availablePeriods = useMemo(
    () => buildPeriodTimeline(periodOptions.years, periodOptions.quarters),
    [periodOptions.quarters, periodOptions.years]
  );

  const selectedPeriod = useMemo(
    () => ({
      yil: toNumber(periodForm.yil, savedPeriod.yil),
      chorak: toNumber(periodForm.chorak, savedPeriod.chorak)
    }),
    [periodForm.chorak, periodForm.yil, savedPeriod.chorak, savedPeriod.yil]
  );

  const selectedKey = buildPeriodKey(selectedPeriod.yil, selectedPeriod.chorak);
  const selectedSummary = periodMap[selectedKey] || { yil: selectedPeriod.yil, chorak: selectedPeriod.chorak, count: 0 };
  const currentLabel = formatPeriodLabel(selectedPeriod.yil, selectedPeriod.chorak);
  const isDirty =
    toNumber(periodForm.yil) !== toNumber(savedPeriod.yil) || toNumber(periodForm.chorak) !== toNumber(savedPeriod.chorak);

  const previousPeriod = useMemo(() => getShiftedPeriod(selectedPeriod, availablePeriods, -1), [availablePeriods, selectedPeriod]);
  const nextPeriod = useMemo(() => getShiftedPeriod(selectedPeriod, availablePeriods, 1), [availablePeriods, selectedPeriod]);
  const previousCount = previousPeriod ? toNumber(periodMap[buildPeriodKey(previousPeriod.yil, previousPeriod.chorak)]?.count, 0) : 0;
  const trendDelta = selectedSummary.count - previousCount;
  const trendText =
    previousPeriod && previousCount > 0
      ? `${trendDelta > 0 ? '+' : ''}${trendDelta} ta`
      : selectedSummary.count > 0
      ? 'yangi oqim'
      : 'hali yozuv yo`q';

  const totalResults = (periodSummary || []).reduce((sum, item) => sum + toNumber(item.count), 0);
  const busiestPeriod = useMemo(() => {
    if (!(periodSummary || []).length) {
      return null;
    }

    return [...periodSummary].sort((left, right) => toNumber(right.count) - toNumber(left.count))[0];
  }, [periodSummary]);

  const selectedYearTotal = (periodSummary || [])
    .filter(item => toNumber(item.yil) === selectedPeriod.yil)
    .reduce((sum, item) => sum + toNumber(item.count), 0);

  const yearCards = useMemo(
    () =>
      (periodOptions.years || []).map(yil => ({
        yil,
        count: (periodSummary || [])
          .filter(item => toNumber(item.yil) === yil)
          .reduce((sum, item) => sum + toNumber(item.count), 0)
      })),
    [periodOptions.years, periodSummary]
  );

  const quarterCards = useMemo(
    () =>
      (periodOptions.quarters || []).map(chorak => ({
        chorak,
        count: toNumber(periodMap[buildPeriodKey(selectedPeriod.yil, chorak)]?.count, 0)
      })),
    [periodMap, periodOptions.quarters, selectedPeriod.yil]
  );

  const summaryPeak = Math.max(...(periodSummary || []).map(item => toNumber(item.count)), 0);
  const historyCards = useMemo(() => (periodSummary || []).slice(0, 12), [periodSummary]);

  const impactCards = [
    {
      title: 'Bosh sahifa statistikasi',
      icon: 'chart-line',
      note: 'Yuqoridagi kartalar va asosiy davrga oid sonlar shu sozlama bilan yangilanadi.'
    },
    {
      title: 'Tahlillar oynalari',
      icon: 'stream',
      note: 'Temperament, suitsid, depressiya va boshqa testlar tanlangan davr bo`yicha o`qiladi.'
    },
    {
      title: 'Psixologik pasport',
      icon: 'id-card',
      note: 'Talaba pasportidagi test natijalari aktiv yil va chorakga mos holda shakllanadi.'
    },
    {
      title: 'Yangi test yozuvlari',
      icon: 'clipboard-list',
      note: 'Saqlangan davrdan keyin tushadigan yangi natijalar bazada shu davr bilan yoziladi.'
    }
  ];

  const selectPeriod = nextSelection => {
    if (!periodPermissions.manage || !nextSelection) {
      return;
    }

    setPeriodForm({
      yil: String(nextSelection.yil),
      chorak: String(nextSelection.chorak)
    });
  };

  const handleFieldChange = ({ target }) => {
    const { name, value } = target;
    setPeriodForm(current => ({
      ...current,
      [name]: value
    }));
  };

  const handlePeriodSave = async () => {
    if (!periodPermissions.manage) {
      return;
    }

    setPeriodSaving(true);
    setPeriodBanner(null);

    try {
      const response = await axios.post(
        TEST_PERIOD_ENDPOINT,
        {
          yil: selectedPeriod.yil,
          chorak: selectedPeriod.chorak
        },
        requestConfig
      );

      if (response.data && response.data.success) {
        const data = response.data.data || {};
        const period = data.period || {};
        const selected = period.selected || {};
        const options = period.options || {};
        const nextSavedPeriod = {
          yil: toNumber(selected.yil, selectedPeriod.yil),
          chorak: toNumber(selected.chorak, selectedPeriod.chorak)
        };

        setPeriodPermissions(data.permissions || periodPermissions);
        setPeriodOptions({
          years: sortNumericValues(options.years || []),
          quarters: sortNumericValues(options.quarters || [])
        });
        setPeriodSummary(period.summary || []);
        setSavedPeriod(nextSavedPeriod);
        setPeriodForm({
          yil: String(nextSavedPeriod.yil),
          chorak: String(nextSavedPeriod.chorak)
        });
        setPeriodBanner({
          color: 'success',
          text: response.data.message || "Test davri sozlamalari yangilandi."
        });
        return;
      }

      throw new Error((response.data && response.data.message) || "Test davri sozlamalarini saqlab bo'lmadi.");
    } catch (error) {
      setPeriodBanner({
        color: 'danger',
        text: error.response?.data?.message || error.message || "Test davri sozlamalarini saqlashda xatolik yuz berdi."
      });
    } finally {
      setPeriodSaving(false);
    }
  };

  const selectionDisabled = periodLoading || periodSaving || !periodPermissions.manage;

  return (
    <>
      <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-3">
        <div className="mb-3 mb-xl-0">
          <Badge color="soft-primary" pill className="mb-2">
            Test sozlamalari
          </Badge>
          <h3 className="mb-1">Yil va chorak boshqaruvi</h3>
          <p className="text-600 mb-0">
            Faol test davrini boshqaring, bazadagi oqimni kuzating va qaysi davr butun modulga ta`sir qilayotganini bir
            oynada nazorat qiling.
          </p>
        </div>

        <div className="d-flex flex-wrap">
          <Badge color={isDirty ? 'soft-warning' : 'soft-success'} pill className="mr-2 mb-2">
            {isDirty ? "Saqlanmagan o'zgarish" : 'Bazada faol'}
          </Badge>
          <Badge color="soft-info" pill className="mb-2">
            {currentLabel}
          </Badge>
        </div>
      </div>

      <Row>
        <Col xl={4} className="mb-3">
          <Card className="border-0 shadow-sm mb-3">
            <CardBody className="med-settings-shell-card">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <Badge color="soft-primary" pill>
                    Sozlamalar
                  </Badge>
                  <h5 className="mb-1 mt-2">Bo'limlar ko'rinishi</h5>
                  <p className="text-500 mb-0">Rasmdagi kabi asosiy bo'limlar shu joyda tartibli ko'rinadi.</p>
                </div>
                <div className="med-settings-shell-icon">
                  <FontAwesomeIcon icon="cog" />
                </div>
              </div>

              <div className="med-period-settings-layout">{renderSettingsGroups(visibleSettingsGroups, location.pathname)}</div>
            </CardBody>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardBody className="med-settings-shell-card">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <Badge color="soft-info" pill>
                    Test paneli
                  </Badge>
                  <h5 className="mb-1 mt-2">Tezkor yo'nalishlar</h5>
                  <p className="text-500 mb-0">Test sozlamalari ichidagi bo'limlarga shu yerdan o'tiladi.</p>
                </div>
                <div className="med-settings-shell-icon">
                  <FontAwesomeIcon icon="stream" />
                </div>
              </div>

              <div className="med-period-quick-links">
                {visibleTestSettingsItems.map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={classNames('med-period-quick-link', {
                      'is-active': location.pathname === item.to
                    })}
                  >
                    <span>{item.name}</span>
                    <FontAwesomeIcon icon="angle-right" />
                  </Link>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={8}>
          {periodBanner ? (
            <Alert color={periodBanner.color} className="mb-3">
              {periodBanner.text}
            </Alert>
          ) : null}

          {!periodPermissions.view && !periodLoading ? (
            <Card className="border-0 shadow-sm mb-3">
              <CardBody className="med-settings-placeholder">
                <div className="med-settings-placeholder-icon">
                  <FontAwesomeIcon icon="lock" />
                </div>
                <h5 className="mb-2">Ruxsat talab qilinadi</h5>
                <p className="text-500 mb-0">Bu bo'limni ko'rish uchun sizga test sozlamalari bo'yicha ruxsat kerak.</p>
              </CardBody>
            </Card>
          ) : (
            <>
              <Card className="border-0 shadow-sm mb-3 med-period-hero-card">
                <CardBody className="med-period-hero">
                  <div className="med-period-hero__main">
                    <div className="med-period-hero__copy">
                      <Badge color="soft-primary" pill className="mb-2">
                        Faol test davri
                      </Badge>
                      <h2 className="mb-2">{currentLabel}</h2>
                      <p className="mb-0">
                        Tanlangan davr yangi `test_boshqaruv` yozuvlariga yoziladi va dashboard, tahlillar, xavf guruhlari
                        hamda pasport moduli shu asosda filtrlanadi.
                      </p>
                    </div>

                    <div className="med-period-hero__icon">
                      <FontAwesomeIcon icon="calendar-check" />
                    </div>
                  </div>

                  <div className="med-period-hero__toolbar">
                    <div className="med-period-hero__nav">
                      <Button color="light" onClick={() => selectPeriod(previousPeriod)} disabled={!previousPeriod || selectionDisabled}>
                        <FontAwesomeIcon icon="arrow-left" className="mr-2" />
                        Oldingi
                      </Button>
                      <Button color="light" onClick={() => selectPeriod(nextPeriod)} disabled={!nextPeriod || selectionDisabled}>
                        Keyingi
                        <FontAwesomeIcon icon="arrow-right" className="ml-2" />
                      </Button>
                      <Button color="light" onClick={() => setPeriodForm({ yil: String(savedPeriod.yil), chorak: String(savedPeriod.chorak) })} disabled={!isDirty || periodSaving}>
                        <FontAwesomeIcon icon="sync-alt" className="mr-2" />
                        Qaytarish
                      </Button>
                    </div>

                    <div className="med-period-hero__save">
                      {periodPermissions.manage ? (
                        <Button color="primary" onClick={handlePeriodSave} disabled={!isDirty || periodSaving}>
                          {periodSaving ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="check-circle" className="mr-2" />}
                          Saqlash
                        </Button>
                      ) : (
                        <Badge color="soft-warning" pill>
                          Faqat ko'rish rejimi
                        </Badge>
                      )}
                    </div>
                  </div>

                  {periodLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" className="mb-3" />
                      <div className="text-600">Davr ma'lumotlari yuklanmoqda...</div>
                    </div>
                  ) : (
                    <Row className="med-period-stat-grid">
                      <Col md={6} xl={3} className="mb-3">
                        <div className="med-period-stat-card">
                          <span>Joriy davr testi</span>
                          <strong>{selectedSummary.count}</strong>
                          <small>tanlangan yozuvlar</small>
                        </div>
                      </Col>
                      <Col md={6} xl={3} className="mb-3">
                        <div className="med-period-stat-card">
                          <span>Tanlangan yil oqimi</span>
                          <strong>{selectedYearTotal}</strong>
                          <small>{selectedPeriod.yil} bo'yicha jami</small>
                        </div>
                      </Col>
                      <Col md={6} xl={3} className="mb-3">
                        <div className="med-period-stat-card">
                          <span>Eng kuchli davr</span>
                          <strong>{busiestPeriod ? busiestPeriod.count : 0}</strong>
                          <small>{busiestPeriod ? formatPeriodLabel(busiestPeriod.yil, busiestPeriod.chorak) : "Ma'lumot yo'q"}</small>
                        </div>
                      </Col>
                      <Col md={6} xl={3} className="mb-3">
                        <div className="med-period-stat-card">
                          <span>Trend</span>
                          <strong>{trendText}</strong>
                          <small>{previousPeriod ? formatPeriodLabel(previousPeriod.yil, previousPeriod.chorak) : 'oldingi davr yo`q'}</small>
                        </div>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>

              <Row>
                <Col lg={5} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="med-settings-shell-card">
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div>
                          <Badge color="soft-info" pill>
                            Yil tanlash
                          </Badge>
                          <h5 className="mb-1 mt-2">Qaysi yil faol bo'ladi?</h5>
                          <p className="text-500 mb-0">Yillar bo'yicha oqimni ko'rib, kerakli davrni tanlang.</p>
                        </div>
                        <div className="med-settings-shell-icon">
                          <FontAwesomeIcon icon="clock" />
                        </div>
                      </div>

                      <div className="med-period-year-grid">
                        {yearCards.map(item => (
                          <button
                            key={item.yil}
                            type="button"
                            className={classNames('med-period-year-card', {
                              'is-active': selectedPeriod.yil === item.yil
                            })}
                            onClick={() => selectPeriod({ yil: item.yil, chorak: selectedPeriod.chorak })}
                            disabled={selectionDisabled}
                          >
                            <strong>{item.yil}</strong>
                            <span>{item.count} ta test oqimi</span>
                          </button>
                        ))}
                      </div>

                      <div className="med-period-select-wrap mt-3">
                        <Input
                          type="select"
                          name="yil"
                          value={periodForm.yil}
                          onChange={handleFieldChange}
                          disabled={selectionDisabled}
                          className="med-settings-select"
                        >
                          {(periodOptions.years || []).map(year => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={7} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="med-settings-shell-card">
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div>
                          <Badge color="soft-warning" pill>
                            Chorak boshqaruvi
                          </Badge>
                          <h5 className="mb-1 mt-2">Yil ichidagi choraklar</h5>
                          <p className="text-500 mb-0">Har bir chorak kartasida shu yilga tegishli test oqimi ko'rinadi.</p>
                        </div>
                        <Badge color="soft-primary" pill>
                          {selectedYearTotal} ta oqim
                        </Badge>
                      </div>

                      <div className="med-period-quarter-grid">
                        {quarterCards.map(item => (
                          <button
                            key={`${selectedPeriod.yil}-${item.chorak}`}
                            type="button"
                            className={classNames('med-period-quarter-card', {
                              'is-active': selectedPeriod.chorak === item.chorak
                            })}
                            onClick={() => selectPeriod({ yil: selectedPeriod.yil, chorak: item.chorak })}
                            disabled={selectionDisabled}
                          >
                            <div className="med-period-quarter-card__head">
                              <span>{item.chorak}-chorak</span>
                              <Badge color={item.count > 0 ? 'soft-success' : 'soft-secondary'} pill>
                                {item.count}
                              </Badge>
                            </div>
                            <strong>{formatPeriodLabel(selectedPeriod.yil, item.chorak)}</strong>
                            <small>{item.count > 0 ? 'bazada yozuv mavjud' : "hali yozuv yo'q"}</small>
                          </button>
                        ))}
                      </div>

                      <div className="med-period-select-wrap mt-3">
                        <Input
                          type="select"
                          name="chorak"
                          value={periodForm.chorak}
                          onChange={handleFieldChange}
                          disabled={selectionDisabled}
                          className="med-settings-select"
                        >
                          {(periodOptions.quarters || []).map(quarter => (
                            <option key={quarter} value={quarter}>
                              {quarter}-chorak
                            </option>
                          ))}
                        </Input>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="med-settings-shell-card">
                  <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
                    <div>
                      <Badge color="soft-success" pill>
                        Ta'sir doirasi
                      </Badge>
                      <h5 className="mb-1 mt-2">Bu sozlama qayerlarga ishlaydi?</h5>
                      <p className="text-500 mb-0">Davr o'zgarganda qaysi bloklar avtomatik yangilanishini shu yerdan ko'rasiz.</p>
                    </div>
                    <Badge color="soft-secondary" pill>
                      {totalResults} ta jami test oqimi
                    </Badge>
                  </div>

                  <Row>
                    {impactCards.map(item => (
                      <Col md={6} key={item.title} className="mb-3">
                        <div className="med-period-impact-card">
                          <div className="med-period-impact-card__icon">
                            <FontAwesomeIcon icon={item.icon} />
                          </div>
                          <div className="med-period-impact-card__copy">
                            <strong>{item.title}</strong>
                            <span>{item.note}</span>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardBody className="med-settings-shell-card">
                  <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
                    <div>
                      <Badge color="soft-info" pill>
                        Davrlar oqimi
                      </Badge>
                      <h5 className="mb-1 mt-2">Bazadagi davrlar kesimi</h5>
                      <p className="text-500 mb-0">Eng oxirgi davrlar va ulardagi test oqimi shu yerda ko'rinadi.</p>
                    </div>
                    <Badge color="soft-primary" pill>
                      {(periodSummary || []).length} ta davr
                    </Badge>
                  </div>

                  {(historyCards || []).length > 0 ? (
                    <div className="med-period-history-grid">
                      {historyCards.map(item => {
                        const itemCount = toNumber(item.count);
                        const itemPeriod = { yil: toNumber(item.yil), chorak: toNumber(item.chorak) };
                        const isSelected = itemPeriod.yil === selectedPeriod.yil && itemPeriod.chorak === selectedPeriod.chorak;

                        return (
                          <div
                            key={buildPeriodKey(itemPeriod.yil, itemPeriod.chorak)}
                            className={classNames('med-period-history-card', {
                              'is-active': isSelected
                            })}
                          >
                            <div className="med-period-history-card__top">
                              <div>
                                <strong>{formatPeriodLabel(itemPeriod.yil, itemPeriod.chorak)}</strong>
                                <span>{itemCount} ta test oqimi</span>
                              </div>
                              {periodPermissions.manage ? (
                                <Button color="link" className="med-period-history-card__action" onClick={() => selectPeriod(itemPeriod)}>
                                  Tanlash
                                </Button>
                              ) : (
                                <Badge color={isSelected ? 'soft-success' : 'soft-secondary'} pill>
                                  {isSelected ? 'Faol' : 'Arxiv ko`rinish'}
                                </Badge>
                              )}
                            </div>

                            <Progress value={summaryPeak > 0 ? (itemCount / summaryPeak) * 100 : 0} color={isSelected ? 'primary' : 'info'} />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-500">Hozircha davrlar kesimi bo'yicha test yozuvlari topilmadi.</div>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

TestPeriodSettings.propTypes = {
  location: PropTypes.object.isRequired
};

export default withRouter(TestPeriodSettings);
